import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  Intro,
  Filter,
  Field,
  DateRange,
  Pagination,
  Loader,
  Text,
  Title,
  Image,
} from "components/commons";
import DownloadCard from "components/commons/download-card/download-card";
import { useApi, useMount } from "hooks";
import { getReportList } from "apis";
import { DownloadsContext } from "contexts";
import { ReportStatus } from "enums";
import { GuyWithMagGlass } from "images";
import { DOWNLOAD_REPORT_TYPES, REPORT_UPDATED_EVENT } from "utils";
import locale from "localization";
import styles from "./downloads.module.scss";

const NoResult = ({
  title = locale.sorryNoResultFound,
  descriptions = [locale.weCouldNotFindAMatchForSearchCriteria, locale.pleaseTryADifferentOne],
}) => (
  <div className={styles.noSearchResultRender}>
    <div>
      <Image src={GuyWithMagGlass} />
      <Title small>{title}</Title>
      {descriptions.map((description) => (
        <Text key={description} subtitle>
          {description}
        </Text>
      ))}
    </div>
  </div>
);

const DownloadsModule = () => {
  const isPolling = useRef(false);

  const { newDownloads, syncDownloads } = useContext(DownloadsContext);
  const [hasFilter, setHasFilter] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [startEndDate, setStartEndDate] = useState({ startDate: null, endDate: null });

  const {
    request: fetchReports,
    loading,
    result,
  } = useApi({
    api: getReportList,
    handleOwnError: true,
  });

  const fetchReportsRef = useRef(fetchReports);
  fetchReportsRef.current = fetchReports;

  const loadReports = useCallback(
    async ({
      p = page,
      size = perPage,
      startDate = startEndDate.startDate,
      endDate = startEndDate.endDate,
    } = {}) => {
      await fetchReportsRef.current({
        page: p,
        perPage: size,
        reportTypes: DOWNLOAD_REPORT_TYPES,
        startDate,
        endDate,
      });
      isPolling.current = false;
    },
    [page, perPage, startEndDate],
  );

  useMount(() => {
    loadReports({ p: 1 });
  });

  const reports = useMemo(() => result?.reports || [], [result?.reports]);
  const count = result?.count || 0;

  useEffect(() => {
    syncDownloads(reports);
  }, [reports, syncDownloads]);

  useEffect(() => {
    const onReportUpdated = () => {
      isPolling.current = true;
      loadReports({ p: page });
    };
    window.addEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
    return () => window.removeEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
  }, [loadReports, page]);

  const handleApplyFilter = useCallback(() => {
    setHasFilter(!!(startEndDate.startDate || startEndDate.endDate));
    setPage(1);
    loadReports({ p: 1 });
  }, [loadReports, startEndDate]);

  const handleClearFilter = useCallback(() => {
    setHasFilter(false);
    setStartEndDate({ startDate: null, endDate: null });
    setPage(1);
    loadReports({ p: 1, startDate: null, endDate: null });
  }, [loadReports]);

  const handleChangePage = useCallback(
    (p) => {
      setPage(p);
      loadReports({ p });
    },
    [loadReports],
  );

  const handleChangePageSize = useCallback(
    (size) => {
      setPerPage(size);
      setPage(1);
      loadReports({ p: 1, size });
    },
    [loadReports],
  );

  return (
    <>
      <Intro title={locale.downloads} />

      <div className={styles.container}>
        <Filter submit={handleApplyFilter} clear={handleClearFilter}>
          <Field
            label={locale.dateOfGeneration}
            labelPosition="left"
            childrenClassName={styles.fieldContent}
            className={styles.fieldLabel}
          >
            <DateRange value={startEndDate} onChange={(_, { value }) => setStartEndDate(value)} />
          </Field>
        </Filter>
      </div>

      <div className={styles.reports}>
        {loading && !isPolling.current ? (
          <Loader open={loading} />
        ) : reports.length === 0 ? (
          hasFilter ? (
            <NoResult
              title={locale.noMatchingExportsFound}
              descriptions={[locale.tryChangingYourFilters]}
            />
          ) : (
            <NoResult
              title={locale.noExportsYet}
              descriptions={[locale.exportAReportToSeeItsProgressAndDownloadItHere]}
            />
          )
        ) : (
          reports.map((report) => (
            <DownloadCard
              key={report.reportId}
              data={report}
              isNew={
                report.status === ReportStatus.Success &&
                newDownloads.includes(String(report.reportId))
              }
              reloadData={() => loadReports({ p: page })}
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        pageSize={perPage}
        dataCount={count}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
      />
    </>
  );
};

export default DownloadsModule;
